import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useControlCash } from "../store/controlCashStore";
import { useConfirm } from "../contexts/ConfirmContext";
import { usePersistedFormState } from "../hooks/usePersistedFormState";

const EditMonth = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { dispatch, getMonthById, getMonthIncomes, toStorageAmount, toDisplayAmount } = useControlCash();
  const { confirm } = useConfirm();
  
  const month = getMonthById(id);

  const defaultIncomes = [];
  const [incomes, setIncomes, clearPersisted, wasRestored] = usePersistedFormState(`editmonth_${id}`, defaultIncomes);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!month) {
      navigate("/404");
      return;
    }

    // Si se restauró desde storage, no sobrescribir
    if (wasRestored) {
      setIsInitialized(true);
      return;
    }

    // Solo inicializar una vez
    if (!isInitialized) {
      const existingIncomes = getMonthIncomes(id);

      if (existingIncomes.length > 0) {
        setIncomes(existingIncomes.map(inc => ({
          id: inc.id,
          name: inc.name,
          amount: toDisplayAmount(inc.amount).toString(),
          isExisting: true
        })));
      } else {
        setIncomes([{ id: Date.now(), name: "", amount: "", isExisting: false }]);
      }

      setIsInitialized(true);
    }
  }, [month, id, navigate, isInitialized, getMonthIncomes, toDisplayAmount, wasRestored, setIncomes]);

  const addIncome = () => {
    setIncomes([
      ...incomes,
      { id: Date.now(), name: "", amount: "", isExisting: false }
    ]);
  };

  const removeIncome = (incomeId) => {
    const income = incomes.find(inc => inc.id === incomeId);
    
    if (income.isExisting) {
      // Si es un ingreso existente, eliminarlo del store
      confirm({
        title: "Eliminar ingreso",
        message: "¿Estás seguro de eliminar este ingreso?",
        confirmLabel: "Eliminar"
      }).then((ok) => {
        if (ok) {
          dispatch({ type: "DELETE_INCOME", payload: { incomeId } });
          setIncomes(incomes.filter(inc => inc.id !== incomeId));
        }
      });
    } else {
      // Si es nuevo (no guardado), solo quitarlo de la lista
      setIncomes(incomes.filter(inc => inc.id !== incomeId));
    }
  };

  const updateIncome = (incomeId, field, value) => {
    setIncomes(
      incomes.map(inc =>
        inc.id === incomeId ? { ...inc, [field]: value } : inc
      )
    );
    // Limpiar error del campo
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`income_${incomeId}_${field}`];
      return newErrors;
    });
  };

  const validate = () => {
    const newErrors = {};
    let hasValidIncome = false;

    incomes.forEach(income => {
      if (!income.name.trim()) {
        newErrors[`income_${income.id}_name`] = "Nombre requerido";
      }
      if (!income.amount || parseFloat(income.amount) <= 0) {
        newErrors[`income_${income.id}_amount`] = "Monto debe ser mayor a 0";
      } else {
        hasValidIncome = true;
      }
    });

    if (!hasValidIncome) {
      newErrors.noValidIncomes = "Debe haber al menos un ingreso válido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    setIsLoading(true);

    // Obtener ingresos existentes del store
    const existingIncomesFromStore = getMonthIncomes(id);
    const validIncomes = incomes.filter(inc => inc.name.trim() && parseFloat(inc.amount) > 0);

    // 1. Eliminar ingresos que ya no están en la lista
    const validIncomeIds = validIncomes.map(inc => inc.id);
    existingIncomesFromStore.forEach(existingIncome => {
      if (!validIncomeIds.includes(existingIncome.id)) {
        dispatch({
          type: "DELETE_INCOME",
          payload: {
            incomeId: existingIncome.id
          }
        });
      }
    });

    // 2. Actualizar o crear ingresos
    validIncomes.forEach(inc => {
      const amountToStore = toStorageAmount(parseFloat(inc.amount));
      if (inc.isExisting) {
        // Actualizar ingreso existente
        dispatch({
          type: "UPDATE_INCOME",
          payload: {
            incomeId: inc.id,
            name: inc.name.trim(),
            amount: amountToStore
          }
        });
      } else {
        // Crear nuevo ingreso
        dispatch({
          type: "ADD_INCOME",
          payload: {
            monthId: id,
            name: inc.name.trim(),
            amount: amountToStore,
            date: new Date().toISOString()
          }
        });
      }
    });

    clearPersisted();
    setTimeout(() => {
      setIsLoading(false);
      navigate(`/month/${id}`);
    }, 300);
  };

  const handleCancel = () => {
    clearPersisted();
    navigate(`/month/${id}`);
  };

  if (!month) {
    return null;
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
            aria-label="Volver"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver
          </button>
          <h1 className="text-3xl font-bold text-gray-900">
            Editar Ingresos
          </h1>
          <p className="text-gray-600 mt-2">
            {month.name}
          </p>
        </div>

        {/* Ingresos */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ingresos del mes
            </h2>
            <button
              onClick={addIncome}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
              aria-label="Agregar ingreso"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Agregar
            </button>
          </div>

          <div className="space-y-4">
            {incomes.map((income, index) => (
              <div
                key={income.id}
                className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200 hover:border-green-300 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Nombre */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Nombre del ingreso
                      </label>
                      <input
                        type="text"
                        value={income.name}
                        onChange={(e) => updateIncome(income.id, "name", e.target.value)}
                        placeholder="Ej: Salario, Freelance..."
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          errors[`income_${income.id}_name`]
                            ? "border-red-300 bg-red-50"
                            : "border-gray-300"
                        }`}
                      />
                      {errors[`income_${income.id}_name`] && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors[`income_${income.id}_name`]}
                        </p>
                      )}
                    </div>

                    {/* Monto */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">
                        Monto
                      </label>
                      <input
                        type="number"
                        value={income.amount}
                        onChange={(e) => updateIncome(income.id, "amount", e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        className={`w-full px-4 py-2 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                          errors[`income_${income.id}_amount`]
                            ? "border-red-300 bg-red-50"
                            : "border-gray-300"
                        }`}
                      />
                      {errors[`income_${income.id}_amount`] && (
                        <p className="text-red-600 text-sm mt-1">
                          {errors[`income_${income.id}_amount`]}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Botón eliminar */}
                  {incomes.length > 1 && (
                    <button
                      onClick={() => removeIncome(income.id)}
                      className="mt-7 p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      aria-label="Eliminar ingreso"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Errores generales */}
        {errors.noValidIncomes && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {errors.noValidIncomes}
            </p>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pb-8">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Cancelar"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading}
            className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            aria-label="Guardar cambios"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Guardando...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Guardar cambios
              </>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default EditMonth;
